<template>

  <v-container fluid class="px-0 pt-0 shipper-item">

    <!-- Breadcrumb & Actions -->
    <v-row class="mt-2 mb-5">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <template v-if="$root.activeUserHasRole('admin')">
          <v-icon small class="mr-2">mdi-link</v-icon>
          <router-link :to="{ name: 'provider-connection-list' }">Shipper Accounts</router-link>
          <div class="separator">/</div>
        </template>
        <template v-else-if="$root.activeUserHasRole('sys-admin')">
          <v-icon small class="mr-2">mdi-truck</v-icon>
          <router-link :to="{ name: 'shipper-list' }">Shipper Locations</router-link>
          <div class="separator">/</div>
        </template>
        <div class="active-item" v-if="shipper">{{ shipper.name }}</div>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container v-if="isDataLoading" class="d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
    </v-container>

    <template v-else-if="shipper">
      <!---------------------------------------->
      <!-- Location Name, Status, and Actions -->
      <!---------------------------------------->
      <v-container fluid>
        <v-row>
          <v-col cols="12" xs="12" sm="12" md="12" lg="4">
            <div class="page-super-title">Shipper Location</div>
            <div class="page-title">{{ shipper.name }}</div>
            <div class="mt-5">
              <span class="form-field-label">Status:</span>
              <v-chip label outlined :class="`${shipper.shipper_status} ml-3`">
                {{ shipper.shipper_status }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="12" lg="8" class="d-flex align-center justify-start" v-if="canEdit">
            <location-status-manager
                :uiType="uiType"
                :location="shipper"
                @updated="loadShipper" />
          </v-col>
        </v-row>
      </v-container>

      <!---------------------------------------------->
      <!-- Other Names, Phone, Appointment Handling -->
      <!---------------------------------------------->
      <v-container fluid class="mt-4 pb-5 pt-4 page-content">
        <!-- Other Names -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="pt-0">
            <span class="title">Other Names</span>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="7" lg="8" class="pt-0 d-flex align-center justify-sm-center justify-lg-start">
            <v-btn color="orange darken-3" v-if="canEdit" @click="editItemDetails">
              Edit Details
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0">
          <v-col cols="12">
            <div>
              <span class="form-field-label">DBA:</span> {{ shipper.dba }}
            </div>
            <div class="mt-5">
              <span class="form-field-label">Legal Entity Name:</span> {{ shipper.legal_entity_name }}
            </div>
          </v-col>
        </v-row>

        <!-- Phone Numbers -->
        <v-row class="pa-0 ma-0 mt-5">
          <v-col cols="12">
            <span class="title">Phone Numbers</span>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0">
          <v-col cols="12">
            <div>
              <span class="form-field-label">main:</span>
              {{ shipper.phone }}
            </div>
            <div class="mt-5">
              <span class="form-field-label">secondary:</span>
              {{ shipper.phone_secondary }}
            </div>
          </v-col>
        </v-row>

        <!-- Appointment Handling -->
        <v-row class="pa-0 ma-0 mt-5">
          <v-col cols="12">
            <span class="title">Appointment Handling</span>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0">
          <v-col cols="12">
            <div>
              <span class="mr-2 form-field-label">Requires appointment?</span>
              <template v-if="shipper.requires_appointment">
                <span class="mr-3">Yes</span>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" style="font-size:22px;cursor:pointer;" v-bind="attrs" v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div>This will prevent a delivery from being dispatched until appointment is confirmed by the shipper.</div>
                </v-tooltip>
              </template>
              <span v-else>No</span>
            </div>
            <div class="mt-5">
              <span class="form-field-label">Appointment URL:</span>
              {{ shipper.appointment_url }}
            </div>
            <div class="mt-5">
              <span class="form-field-label">Notes:</span>
              {{ shipper.notes }}
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!----------------------------------->
      <!-- Address and Licenses with Map -->
      <!----------------------------------->
      <v-container fluid class="mt-5 pb-5 page-content">
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="pt-0">
            <!-- <v-icon color="white" class="mr-1">mdi-map-marker-outline</v-icon> -->
            <span class="title">Ship-to Address</span>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="7" lg="8" class="pt-0 d-flex align-center justify-sm-center justify-lg-start" v-if="canEditLicenses">
            <v-btn color="orange darken-3" @click="editItemLicenses">
              Edit Licenses and Address
            </v-btn>
            <!-- (note on changes requiring approval) -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="grey lighten-1"
                    style="cursor:pointer;margin-left:10px"
                    v-bind="attrs"
                    v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Making changes to the address and licenses will require approval from {{ appName }}</div>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Address, Coords, Map -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="5" lg="4">
            <div class="d-flex align-center mt-3">
              <v-icon color="#9c9c9c" class="pr-3">mdi-map-marker-outline</v-icon>
              <div class="address-text">
                <div>{{ multiLineAddress.street_1 }}</div>
                <div v-if="multiLineAddress.street_2">{{ multiLineAddress.street_2 }}</div>
                <div v-if="multiLineAddress.area">{{ multiLineAddress.area }}</div>
              </div>
            </div>
            <!-- <div class="mt-8">
              <span class="form-field-label">Longitude:</span>
              {{ shipper.coords.longitude }}
            </div>
            <div class="mt-6">
              <span class="form-field-label">Latitude:</span>
              {{ shipper.coords.latitude }}
            </div> -->
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="7" lg="8">
            <map-viewer
                height="340px"
                :settings="mapSettings"
                :markers="markers" />
          </v-col>
        </v-row>

        <v-row class="pa-0 ma-0 mt-5">
          <v-col cols="12">
            <span class="title">Facility License(s)</span>
          </v-col>
        </v-row>

        <!-- (no licenses configured) -->
        <v-row class="ma-0 pa-0 mt-2 mb-6" v-if="!hasLicenseInfo">
          <v-col cols="12" sm="12" class="form-field-subtext">
            (No licenses have been configured)
          </v-col>
        </v-row>

        <template v-else>
          <!-- Establishment License -->
          <v-row class="pa-0 ma-0 mt-2" v-if="hasEstablishmentLicenseInfo">
            <v-col cols="12" xs="12" sm="12" md="5" lg="4">
              <div class="mb-8 content-title">
                <v-icon style="font-size:20px;padding-bottom:3px;" class="mr-1">mdi-file-document</v-icon>
                Marijuana Establishment License (Adult-Use)
              </div>
              <template v-if="shipper.license_for_shipping_address === 'establishment_license'">
                <div class="mt-4 form-field-subtext">
                  <v-icon class="mr-1" style="font-size:22px;" color="primary">mdi-alert-decagram</v-icon>
                  Contains address of shipping location.
                </div>
                <div class="mt-10">
                  <span class="form-field-label">Address type:</span>
                  {{ formatLicenseCategory(shipper.establishment_license_category) }}
                </div>
              </template>
              <div class="mt-5">
                <span class="form-field-label">License number:</span>
                {{ shipper.establishment_license_number }}
              </div>
              <div class="mt-5">
                <span class="form-field-label">Issue date:</span>
                {{ formatDate(shipper.establishment_license_issue_date) }}
              </div>
              <div class="mt-5">
                <span class="form-field-label">Expiration:</span>
                {{ formatDate(shipper.establishment_license_expiration) }}
              </div>
              <div class="mt-5" v-if="shipper.establishment_license_amended_date">
                <span class="form-field-label">Amended date:</span>
                {{ formatDate(shipper.establishment_license_amended_date) }}
              </div>
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="7" lg="8" align="center" justify="center">
              <v-progress-circular indeterminate :size="40" style="margin-top:110px" color="primary" v-if="isEstablishmentLicenseLoading" />
              <div class="license-doc-container" v-else-if="establishmentLicense">
                <embed style="border:2px solid #525252;" :src="establishmentLicense" width="100%" height="400" />
              </div>
              <div class="view-license-action-container" v-else-if="shipper.saved_establishment_license">
                <v-btn color="gray" @click="loadSavedEstablishmentLicense">
                  View License
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Dispensary Registration Certificate -->
          <v-row class="pa-0 ma-0 mt-8" v-if="hasDrcInfo">
            <v-col cols="12" xs="12" sm="12" md="5" lg="4">
              <div class="mb-8 content-title">
                <v-icon style="font-size:20px;padding-bottom:3px;" class="mr-1">mdi-file-document</v-icon>
                Dispensary Registration Certificate (Medical)
              </div>
              <template v-if="shipper.license_for_shipping_address === 'drc'">
                <div class="mt-4 form-field-subtext">
                  <v-icon class="mr-1" color="primary">mdi-alert-decagram</v-icon>
                  Contains address of shipping location.
                </div>
                <div class="mt-10">
                  <span class="form-field-label">Address type:</span>
                  {{ formatLicenseCategory(shipper.drc_category) }}
                </div>
              </template>
              <div class="mt-5">
                <span class="form-field-label">License number:</span>
                {{ shipper.drc_number }}
              </div>
              <div class="mt-5">
                <span class="form-field-label">Issue date:</span>
                {{ formatDate(shipper.drc_issue_date) }}
              </div>
              <div class="mt-5">
                <span class="form-field-label">Expiration:</span>
                {{ formatDate(shipper.drc_expiration) }}
              </div>
              <div class="mt-5" v-if="shipper.drc_amended_date">
                <span class="form-field-label">Amended date:</span>
                {{ formatDate(shipper.drc_amended_date) }}
              </div>
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="7" lg="8" align="center" justify="center">
              <v-progress-circular indeterminate :size="40" style="margin-top:110px" color="primary" v-if="isDrcLoading" />
              <div class="license-doc-container" v-else-if="drc">
                <embed style="border:2px solid #525252;" :src="drc" width="100%" height="400" />
              </div>
              <div class="view-license-action-container" v-else-if="shipper.saved_drc">
                <v-btn color="gray" @click="loadSavedDrcLicense">
                  View License
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-container>

      <!-------------------->
      <!-- Contact Emails -->
      <!-------------------->
      <v-container fluid class="mt-5 pb-5 page-content">
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="pt-0">
            <span class="title">Contact Emails</span>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="7" lg="8" class="pt-0 d-flex align-center justify-sm-center justify-lg-start">
            <v-btn color="primary" @click="isEmailDialogOpen = true" v-if="canEdit">
              + Add Email
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0 mt-2">
          <v-col cols="12" xs="12" sm="12" md="12" lg="9">
            <template v-if="emails && emails.length > 0">
              <recipient-email-display
                  v-for="email in emails"
                  :key="email.id"
                  :showActions="canEdit"
                  :recipientEmail="email"
                  v-on:edit="invokeEditEmail(email)"
                  v-on:delete="deleteEmail(email)" />
            </template>
            <div class="form-field-subtext" v-else>
              (No emails added)
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!----------------------------------->
    <!-- Recipient Email Editor Dialog -->
    <!----------------------------------->
    <v-dialog persistent max-width="490" v-model="isEmailDialogOpen">
      <recipient-email-editor
          :recipientEmail="emailToEdit"
          v-on:cancel="closeEditEmail"
          v-on:create="addEmail"
          v-on:update="updateEmail" />
    </v-dialog>

  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
import LocationStatusManager from '@/components/locations/LocationStatusManager'
import RecipientEmailDisplay from '@/components/app/RecipientEmailDisplay'
import RecipientEmailEditor from '@/components/app/RecipientEmailEditor'
import MapViewer from '@/components/map/MapViewer'

export default {
  name: 'ShipperItemPage',

  components: {
    LocationStatusManager,
    RecipientEmailDisplay,
    RecipientEmailEditor,
    MapViewer
  },

  props: {
    shipperId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      isEstablishmentLicenseLoading: false,
      isDrcLoading: false,
      shipper: null,
      map: null,
      establishmentLicense: null,
      drc: null,
      // ---------- Email management
      emailToEdit: null,
      isEmailDialogOpen: false
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    uiType () {
      return this.$root.uiType()
    },
    canEdit () {
      return (this.uiType === 'shipper' && this.$root.activeUserHasRole('admin')) || this.uiType === 'sys-admin'
    },
    canEditLicenses () {
      if (this.uiType === 'sys-admin') {
        return true
      } else {
        return this.canEdit && this.shipper.shipper_status !== 'PENDING_APPROVAL'
      }
    },
    emails () {
      return (this.shipper.recipient_emails)
        ? this.$root.utils.sortBy(this.shipper.recipient_emails, ['created_at'])
        : []
    },
    hasEstablishmentLicenseInfo () {
      return (
        this.shipper.establishment_license_number ||
        this.shipper.establishment_license_issue_date ||
        this.shipper.establishment_license_expiration ||
        this.shipper.saved_establishment_license
      ) ? true : false
    },
    hasDrcInfo () {
      return (
        this.shipper.drc_number ||
        this.shipper.drc_issue_date ||
        this.shipper.drc_expiration
      ) ? true : false
    },
    hasLicenseInfo () {
      return this.hasEstablishmentLicenseInfo || this.hasDrcInfo
    },
    multiLineAddress () {
      return this.$root.utils.getAddressTextFromData(this.shipper, { multi_lines: true })
    },
    locationCoords () {
      return this.shipper.coords || {}
    },
    coordinates () {
      return (this.locationCoords.longitude && this.locationCoords.latitude)
        ? [
          this.$root.utils.truncateDecimals(this.locationCoords.longitude, 5),
          this.$root.utils.truncateDecimals(this.locationCoords.latitude, 5)
        ]
        : null
    },
    markers () {
      return (this.coordinates)
        ? [{ coordinates: this.coordinates, properties: this.markerProperties }]
        : null
    },
    markerProperties () {
      return {
        name: this.shipper.name,
        alias: this.shipper.alias,
        type: 'location'
      }
    },
    mapSettings () {
      if (this.locationCoords.longitude && this.locationCoords.latitude) {
        return {
          center: [this.locationCoords.longitude, this.locationCoords.latitude],
          zoom: 15,
          pitch: 0
        }
      } else {
        return null
      }
    }
  },

  async beforeMount () {
    // Ensure shipper is loaded
    this.loadShipper()
  },

  methods: {
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MM/DD/YYYY') : ''
    },

    formatLicenseCategory (code) {
      switch (code) {
        case 'retail': return 'Retail'
        case 'manufacturing_facility': return 'Manufacturing Facility'
        case 'offsite': return 'Offsite'
        case 'offsite_cultivation': return 'Offsite Cultivation'
        default: return code
      }
    },

    async loadShipper () {
      this.isDataLoading = true

      try {
        this.shipper = await this.$store.dispatch('sh/dataApi', {
          uri: `shipper/${this.shipperId}`,
          opts: {
            params: { with: 'recipient_emails,approved_licenses' }
          }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    async loadSavedEstablishmentLicense () {
      if (this.shipper.saved_establishment_license) {
        this.isEstablishmentLicenseLoading = true
        this.establishmentLicense = await this.$root.fetchLocationAsset(this.shipperId, 'establishment_license')
        this.isEstablishmentLicenseLoading = false
      }
    },

    async loadSavedDrcLicense () {
      if (this.shipper.saved_drc) {
        this.isDrcLoading = true
        this.drc = await this.$root.fetchLocationAsset(this.shipperId, 'drc')
        this.isDrcLoading = false
      }
    },

    editItemDetails () {
      this.$store.dispatch('sh/setItemToEdit', this.shipper)
      this.$root.goTo('shipper-item-edit-details', { shipperId: this.shipperId }) // go to edit page
    },

    editItemLicenses () {
      this.$store.dispatch('sh/setItemToEdit', this.shipper)
      this.$root.goTo('shipper-item-edit-licenses', { shipperId: this.shipperId }) // go to edit page
    },

    invokeEditEmail (recipientEmail = {}) {
      this.emailToEdit = recipientEmail
      this.isEmailDialogOpen = true
    },

    closeEditEmail () {
      this.isEmailDialogOpen = false
      this.emailToEdit = null
    },

    async addEmail (data = {}) {
      this.isEmailDialogOpen = false

      try {
        const bodyData = {
          location_id: this.shipperId,
          ...data
        }
        const email = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'recipientEmail',
          opts: { bodyData }
        })

        // Add email to shipper
        this.shipper.recipient_emails.push(email)
      } catch (err) {
        console.log('Error adding email =>', err)
      }
    },

    async updateEmail (recipientEmail = {}) {
      this.isEmailDialogOpen = false
      if (!recipientEmail.id) return false

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `recipientEmail/${recipientEmail.id}`,
          opts: { bodyData: recipientEmail }
        })
      } catch (err) {
        console.log('Error updating email =>', err)
      }
    },

    async deleteEmail (recipientEmail = {}) {
      if (!recipientEmail.id) return false

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'delete',
          uri: `recipientEmail/${recipientEmail.id}`
        })

        // Remove email from shipper
        const existingEmails = this.shipper.recipient_emails.filter(email => email.id !== recipientEmail.id)
        this.shipper.recipient_emails = existingEmails
      } catch (err) {
        console.log('Error deleting email =>', err)
      }
    }
  }
}
</script>

<style scoped>
  .shipper-item .form-field-label {
    margin-right: 4px;
  }

  .shipper-item .content-title {
    font-size: 18px;
  }

  .shipper-item .section-title {
    color: #9a9a9a;
    font-size: 1.1rem;
  }

  .shipper-item .form-field-subtext {
    color: #9a9a9a;
    font-size: 1.0em;
    font-style: italic;
  }

  .shipper-item .address-text {
    color: #ffffff;
    font-size: 1.1rem;
  }

  .shipper-item .view-license-action-container {
    border: 1px solid #525252;
    border-radius: 4px;
    padding-top: 110px;
    height: 230px;
  }

  .shipper-item .license-doc-container {
    text-align: center;
  }
</style>
